import {
  EXPENSE_TYPE_IRREGULAR,
  EXPENSE_TYPE_ONGOING,
  normalizeExpenseType
} from "../expenseType.js";
import {
  emitProfileDataChanged,
  openDatabase,
  readAllFromIndex,
  STORE_BUDGET_CATEGORIES,
  STORE_EXPENSES,
  STORE_YEARS,
  transactionDone
} from "./walletWatchDbCore.js";
import { ensureProfileYearRecord } from "./walletWatchProfiles.js";

function serializeBudgetCategory(profileId, year, row, existingCategory, now) {
  return {
    id: row.id ?? existingCategory?.id ?? crypto.randomUUID(),
    profileId,
    year,
    name: row.category?.trim() ?? "",
    ongoingBudget: Number(row.ongoingBudget ?? 0),
    irregularBudget: Number(row.irregularBudget ?? 0),
    createdAt: existingCategory?.createdAt ?? now,
    updatedAt: now,
    archivedAt: existingCategory?.archivedAt ?? null
  };
}

function isBudgetCategoryUnchanged(existingCategory, nextCategory) {
  if (!existingCategory) {
    return false;
  }

  return existingCategory.profileId === nextCategory.profileId
    && existingCategory.year === nextCategory.year
    && existingCategory.name === nextCategory.name
    && Number(existingCategory.ongoingBudget ?? 0) === nextCategory.ongoingBudget
    && Number(existingCategory.irregularBudget ?? 0) === nextCategory.irregularBudget
    && (existingCategory.archivedAt ?? null) === nextCategory.archivedAt;
}

function normalizeBudgetRow(category, expenseTotals) {
  const totals = expenseTotals.get(category.id) ?? { ongoingSpent: 0, irregularSpent: 0 };

  return {
    id: category.id,
    category: category.name,
    archived: Boolean(category.archivedAt),
    archivedAt: category.archivedAt ?? null,
    ongoingBudget: category.ongoingBudget ?? 0,
    ongoingSpent: totals.ongoingSpent,
    impliedOngoing: 0,
    ongoingOverrun: 0,
    overrunMeter: 0,
    irregularBudget: category.irregularBudget ?? 0,
    irregularSpent: totals.irregularSpent,
    irregularRemaining: 0
  };
}

function normalizeBudgetCategoryOption(category) {
  return {
    value: category.id,
    label: category.name
  };
}

function normalizeExpenseRow(expense, categoriesById = new Map(), categoryIdsByName = new Map()) {
  const categoryId = expense.categoryId
    ?? categoryIdsByName.get(expense.category ?? "")
    ?? "";
  const category = categoriesById.get(categoryId)?.name
    ?? expense.category
    ?? "";

  return {
    id: expense.id,
    date: expense.date ?? "",
    amount: expense.amount ?? 0,
    method: expense.method ?? "",
    type: normalizeExpenseType(expense.type),
    supplier: expense.supplier ?? "",
    categoryId,
    category,
    service: expense.service ?? "",
    createdAt: expense.createdAt ?? ""
  };
}

function serializeExpenseRow(profileId, year, row, existingExpense, now) {
  return {
    id: row.id ?? existingExpense?.id ?? crypto.randomUUID(),
    profileId,
    year,
    date: row.date?.trim() ?? "",
    amount: Number(row.amount ?? 0),
    method: row.method?.trim() ?? "",
    type: normalizeExpenseType(row.type),
    supplier: row.supplier?.trim() ?? "",
    categoryId: row.categoryId?.trim() ?? "",
    category: row.category?.trim() ?? "",
    service: row.service?.trim() ?? "",
    createdAt: existingExpense?.createdAt ?? now,
    updatedAt: now
  };
}

function isExpenseUnchanged(existingExpense, nextExpense) {
  if (!existingExpense) {
    return false;
  }

  return existingExpense.profileId === nextExpense.profileId
    && existingExpense.year === nextExpense.year
    && existingExpense.date === nextExpense.date
    && Number(existingExpense.amount ?? 0) === nextExpense.amount
    && (existingExpense.method ?? "") === nextExpense.method
    && (existingExpense.type ?? "") === nextExpense.type
    && (existingExpense.supplier ?? "") === nextExpense.supplier
    && (existingExpense.categoryId ?? "") === nextExpense.categoryId
    && (existingExpense.category ?? "") === nextExpense.category
    && (existingExpense.service ?? "") === nextExpense.service;
}

export async function listBudgetRows(profileId, year) {
  const db = await openDatabase();
  const transaction = db.transaction([STORE_BUDGET_CATEGORIES, STORE_EXPENSES], "readonly");
  const categoriesPromise = readAllFromIndex(
    transaction.objectStore(STORE_BUDGET_CATEGORIES),
    "profileYear",
    [profileId, year]
  );
  const expensesPromise = readAllFromIndex(
    transaction.objectStore(STORE_EXPENSES),
    "profileYear",
    [profileId, year]
  );

  const [categories, expenses] = await Promise.all([categoriesPromise, expensesPromise]);
  await transactionDone(transaction);

  const sortedCategories = categories
    .slice()
    .sort((left, right) => left.name.localeCompare(right.name));
  const categoryIdsByName = new Map(categories.map((category) => [category.name, category.id]));
  const expenseTotals = new Map();

  expenses.forEach((expense) => {
    const categoryId = expense.categoryId ?? categoryIdsByName.get(expense.category ?? "");
    if (!categoryId) {
      return;
    }

    const current = expenseTotals.get(categoryId) ?? { ongoingSpent: 0, irregularSpent: 0 };
    const expenseType = normalizeExpenseType(expense.type);

    if (expenseType === EXPENSE_TYPE_ONGOING) {
      current.ongoingSpent += expense.amount ?? 0;
    }

    if (expenseType === EXPENSE_TYPE_IRREGULAR) {
      current.irregularSpent += expense.amount ?? 0;
    }

    expenseTotals.set(categoryId, current);
  });

  return sortedCategories.map((category) => normalizeBudgetRow(category, expenseTotals));
}

export async function listBudgetCategoryOptions(profileId, year) {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_BUDGET_CATEGORIES, "readonly");
  const categories = await readAllFromIndex(transaction.objectStore(STORE_BUDGET_CATEGORIES), "profileYear", [profileId, year]);
  await transactionDone(transaction);

  return categories
    .filter((category) => !category.archivedAt)
    .sort((left, right) => left.name.localeCompare(right.name))
    .map(normalizeBudgetCategoryOption);
}

export async function updateBudgetRows(profileId, year, rows) {
  const db = await openDatabase();
  const transaction = db.transaction([STORE_YEARS, STORE_BUDGET_CATEGORIES, STORE_EXPENSES], "readwrite");
  const yearsStore = transaction.objectStore(STORE_YEARS);
  const store = transaction.objectStore(STORE_BUDGET_CATEGORIES);
  const expensesStore = transaction.objectStore(STORE_EXPENSES);
  const existingCategories = await readAllFromIndex(store, "profileYear", [profileId, year]);
  const existingExpenses = await readAllFromIndex(expensesStore, "profileYear", [profileId, year]);
  const existingById = new Map(existingCategories.map((category) => [category.id, category]));
  const existingCategoryIdsByName = new Map(existingCategories.map((category) => [category.name, category.id]));
  const referencedCategoryIds = new Set(
    existingExpenses
      .map((expense) => expense.categoryId ?? existingCategoryIdsByName.get(expense.category ?? ""))
      .filter(Boolean)
  );
  const now = new Date().toISOString();
  await ensureProfileYearRecord(yearsStore, profileId, year, now);
  const nextIds = new Set();

  rows.forEach((row) => {
    const existing = existingById.get(row.id);
    const nextCategory = serializeBudgetCategory(profileId, year, row, existing, now);
    nextIds.add(nextCategory.id);

    if (isBudgetCategoryUnchanged(existing, nextCategory)) {
      return;
    }

    store.put(nextCategory);
  });

  existingCategories.forEach((category) => {
    if (!nextIds.has(category.id)) {
      if (referencedCategoryIds.has(category.id)) {
        store.put({
          ...category,
          archivedAt: category.archivedAt ? null : now,
          updatedAt: now
        });
        return;
      }

      store.delete(category.id);
    }
  });

  await transactionDone(transaction);
  emitProfileDataChanged({
    profileId,
    reason: "update-budget",
    changeType: "domain-data"
  });
}

export async function listExpenseRows(profileId, year) {
  const db = await openDatabase();
  const transaction = db.transaction([STORE_EXPENSES, STORE_BUDGET_CATEGORIES], "readonly");
  const expensesPromise = readAllFromIndex(transaction.objectStore(STORE_EXPENSES), "profileYear", [profileId, year]);
  const categoriesPromise = readAllFromIndex(
    transaction.objectStore(STORE_BUDGET_CATEGORIES),
    "profileYear",
    [profileId, year]
  );
  const [expenses, categories] = await Promise.all([expensesPromise, categoriesPromise]);
  await transactionDone(transaction);
  const categoriesById = new Map(categories.map((category) => [category.id, category]));
  const categoryIdsByName = new Map(categories.map((category) => [category.name, category.id]));

  return expenses
    .slice()
    .sort((left, right) => {
      const dateComparison = (right.date ?? "").localeCompare(left.date ?? "");

      if (dateComparison !== 0) {
        return dateComparison;
      }

      return (right.createdAt ?? "").localeCompare(left.createdAt ?? "");
    })
    .map((expense) => normalizeExpenseRow(expense, categoriesById, categoryIdsByName));
}

export async function updateExpenseRows(profileId, year, rows, options = {}) {
  const db = await openDatabase();
  const transaction = db.transaction([STORE_YEARS, STORE_EXPENSES, STORE_BUDGET_CATEGORIES], "readwrite");
  const yearsStore = transaction.objectStore(STORE_YEARS);
  const expensesStore = transaction.objectStore(STORE_EXPENSES);
  const budgetCategoriesStore = transaction.objectStore(STORE_BUDGET_CATEGORIES);
  const [existingExpenses, existingCategories] = await Promise.all([
    readAllFromIndex(expensesStore, "profileYear", [profileId, year]),
    readAllFromIndex(budgetCategoriesStore, "profileYear", [profileId, year])
  ]);
  const allowEmptyReplace = options.allowEmptyReplace ?? false;

  if (!allowEmptyReplace && existingExpenses.length > 0 && rows.length === 0) {
    transaction.abort();
    throw new Error("Refusing to replace a non-empty expense set with an empty one.");
  }

  const existingById = new Map(existingExpenses.map((expense) => [expense.id, expense]));
  const now = new Date().toISOString();
  await ensureProfileYearRecord(yearsStore, profileId, year, now);
  const nextIds = new Set();

  rows.forEach((row) => {
    const expenseYear = String(row.date ?? "").slice(0, 4);
    if (expenseYear !== String(year)) {
      transaction.abort();
      throw new Error(`Expense date year must match the active year "${year}".`);
    }

    const existing = existingById.get(row.id);
    const nextExpense = serializeExpenseRow(profileId, year, row, existing, now);
    nextIds.add(nextExpense.id);

    if (isExpenseUnchanged(existing, nextExpense)) {
      return;
    }

    expensesStore.put(nextExpense);
  });

  existingExpenses.forEach((expense) => {
    if (!nextIds.has(expense.id)) {
      expensesStore.delete(expense.id);
    }
  });

  const nextReferencedCategoryIds = new Set(rows.map((row) => String(row.categoryId ?? "").trim()).filter(Boolean));
  existingCategories.forEach((category) => {
    if (!category.archivedAt || nextReferencedCategoryIds.has(category.id)) {
      return;
    }

    budgetCategoriesStore.delete(category.id);
  });

  await transactionDone(transaction);
  emitProfileDataChanged({
    profileId,
    reason: "update-expenses",
    changeType: "domain-data"
  });
}
